/**
 * TypeScript type definitions
 * Shared types for the frontend (matching backend types)
 */

// Re-export backend types that are also used in frontend
export enum Product {
  IM_MATH = 'IM_MATH',
}

export enum ReviewStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export enum ExperimentVersion {
  V0_0_0 = 'v0.0.0',
}

export enum TagPredictionType {
  LessonV1_SpineTagsPrediction = 'LessonV1_SpineTagsPrediction',
}

export enum LessonSubject {
  MATHEMATICS = 'MATHEMATICS',
}

export enum UserRole {
  ADMIN = 'admin',
  USER = 'user',
}

export enum ReportType {
  MISSING_PREDICTIONS = 'MISSING_PREDICTIONS',
  STATUS_BREAKDOWN = 'STATUS_BREAKDOWN',
  REVIEWER_ACTIVITY = 'REVIEWER_ACTIVITY',
}

// User and Auth types
export interface User {
  email: string;
  role: UserRole;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  token: string;
}

// User Preferences
export interface UserPreferences {
  username: string;
  showNonSelectedTags: boolean;
  filterGrades: string[];
  filterDocs: string[];
  filterJurisdictions: string[];
  filterStates: string[];
  standardsSortField: string;
  standardsSortDir: 'asc' | 'desc';
  standardsPreviewTab: string;
  standardsSortExpanded: boolean;
  metricsViewMode: 'all-alignments' | 'filtered';
  standardsViewMode: 'aligned' | 'filtered-aligned' | 'all';
  updatedAt?: Date;
}

// Tag Candidate Structure

/** Limiter type for "mathtastic" limiters (regex-based math expression matching) */
export const LIMITER_TYPE_MATHTASTIC = 'P';

export interface Limiter {
  limiter_id: string;
  description: string;
  type: string;
  selected: boolean;
  confidence_score: number;
  reasoning: string;
}

export interface LimiterGroup {
  limiter_group_id: string;
  description: string;
  selected: boolean;
  confidence_score: number;
  reasoning: string;
  limiters: Limiter[];
  selected_math_expression_ids?: string[];
}

export interface Competency {
  spine_node_id: string;
  description: string;
  selected: boolean;
  confidence_score: number;
  reasoning: string;
}

export interface TagCandidate {
  content_id: string;
  competency: Competency;
  limiter_groups: LimiterGroup[];
}

// Math Expression - Decomposed learning target
export interface DecomposedExpression {
  id: string;
  plainText: string;
  latex: string;
  spine: any;
  explanation: string;
  selected: boolean;
}

// Math Expression - Content expression with SPINE format
export interface ContentExpression {
  id: string;
  plainText: string;
  latex: string;
  spine: any;
  context: string;
  reasoning: string;
  confidence_score: number;
  selected: boolean;
}

// Math Expression - Full expression with decomposed targets
export interface MathExpression {
  contentExpression: ContentExpression;
  decomposed: DecomposedExpression[];
}

// Prediction
export interface LessonV1_SpineTagsPrediction {
  id: string;
  product: Product;
  experiment_version: ExperimentVersion;
  review_status: ReviewStatus;
  tag_prediction_type: TagPredictionType;
  created_at: Date | string;
  updated_at: Date | string;
  reviewed_by: string[];
  feedback?: string;
  assignee?: string;
  lesson_name: string;
  lesson_number: string;
  lesson_subject: LessonSubject;
  lesson_grade: number;
  lesson_pdf: string;
  unit_number: string;
  unit_name: string;
  tag_prediction_original?: TagCandidate[];
  tag_prediction_modified?: TagCandidate[];
  lesson_pdf_extractions?: Record<string, string>;
  math_expressions?: MathExpression[];
  target_cc_standard_codes?: string[];
  target_standard_ids?: string[];
  spine_tag_ids?: string[];
}

// SPINE API Types

/**
 * Unified standard type for frontend use (camelCase)
 */
export interface Standard {
  standardId: string;
  code: string;
  description: string;
  document: string;
  grade: string;
  jurisdiction: string;
  state?: string;
  subject?: string;
}

/**
 * Flattened standard from SPINE service
 * GET /api/spine/flattenedstandards/{uuid}
 */
export interface FlattenedStandard {
  standardId: string;
  standardParentId: string | null;
  ancestors: string[];
  status: string;
  standardSource: string;
  sourceStandardIdAliases: string[];
  grades: number[];
  documentCode: string;
  documentDescription: string | null;
  standardJurisdiction: string;
  usStatePostalCode: string | null;
  subjectCode: string | null;
  subjectDescription: string | null;
  yearDrafted: number;
  yearAdopted: number;
  yearImplemented: number;
  yearAssessed: number;
  yearLastRevised: number;
  yearObsoleted: number;
  domainId: string | null;
  domainCode: string | null;
  domainDescription: string | null;
  clusterId: string | null;
  clusterCode: string | null;
  clusterDescription: string | null;
  phiStandardId: string | null;
  phiStandardCode: string | null;
  phiStandardDescription: string | null;
  chiStandardId: string | null;
  chiStandardCode: string | null;
  chiStandardDescription: string | null;
  psiStandardId: string | null;
  psiStandardCode: string | null;
  psiStandardDescription: string | null;
  omegaStandardId: string | null;
  omegaStandardCode: string | null;
  omegaStandardDescription: string | null;
  standardItemType: string;
  itemCode: string;
  itemDescription: string;
  textSearch: string;
  isDefault: boolean;
}

export interface StandardsPreviewRequest {
  competency_id: string;
  limiter_ids: string[];
  regex_target_composite?: {
    id: string;
    type: string;
    regexTargets: any[];
    order: number;
  };
  selected_math_expression_ids?: string[];
  filters?: {
    grades?: string[];
    documents?: string[];
    jurisdictions?: string[];
    states?: string[];
  };
}

// Pagination
export interface PaginationState {
  currentPage: number;
  pageSize: number;
  lastEvaluatedKey?: Record<string, any>;
  pageHistory: Array<Record<string, any> | null>;
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    currentPage: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    lastEvaluatedKey?: Record<string, any>;
  };
}

// Filters
export interface PredictionFilters {
  grades?: string[];
  units?: string[];
  statuses?: ReviewStatus[];
  reviewers?: string[];
  assignees?: string[];
  searchQuery?: string;
  tagsFilter?: 'has_tags' | 'no_tags';
}

export interface UniqueFilterValues {
  grades: string[];
  units: string[];
  statuses: ReviewStatus[];
  reviewers: string[];
  assignees: string[];
}

// Tag Generation
export interface TagGenerationResult {
  id: string;
  success: boolean;
  tag_ids?: string[];
  error?: string;
}

export interface SingleTagGenerationResult {
  success: boolean;
  tagIds: string[];
  mathExpressionTags?: any[]; // SPINE format
  error?: string;
}

// App Info
export interface AppInfo {
  environment: string;
  awsRegion: string;
  spineApiBaseUrl: string;
  version: string;
  buildTimestamp: string;
  nodeVersion: string;
  platform: string;
  deploymentEnv: string;
  systemName: string;
  adminUsers: string[];
  regularUsers: string[];
}

// Color mapping
export const REVIEW_STATUS_COLORS: Record<ReviewStatus, string> = {
  [ReviewStatus.PENDING]: '#FFD700',
  [ReviewStatus.IN_PROGRESS]: '#007FFF',
  [ReviewStatus.APPROVED]: '#28a745',
  [ReviewStatus.REJECTED]: '#dc3545',
};

// Chart and UI color constants
export const CHART_COLORS = {
  ALIGNED: '#007FFF', // Azure blue - Aligned items
  NOT_ALIGNED: '#808080', // Gray - Not aligned items
  FOCUSED_TAG: '#FFD700', // Yellow - Focused tag
  FOCUSED_STANDARD: '#00FF00', // Green - Focused standard
  BORDER: '#333333', // Dark gray - Borders
};

export const TAG_COLORS = {
  // Use same colors as chart for consistency
  ALIGNED_BORDER: CHART_COLORS.ALIGNED,
  FOCUSED_TAG_BORDER: CHART_COLORS.FOCUSED_TAG,
  FOCUSED_STANDARD_BORDER: CHART_COLORS.FOCUSED_STANDARD,
};

// ============================================================================
// Standards Filter Constants (canned values for dropdowns)
// ============================================================================

export const CANNED_GRADES = [
  'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'
];

export const CANNED_JURISDICTIONS = ['NATIONAL', 'STATE', 'INTERNATIONAL'];

export const CANNED_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL',
  'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
  'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH',
  'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export const CANNED_DOCUMENTS = [
  '2013',
  '21CSF',
  'AASLS',
  'ACTCRS',
  'ACTFL',
  'ACTQC',
  'AK - 1995/Revised 2016',
  'AK - 1998/Revised 2016',
  'AK - 2012',
  'AK - 2012 (WIDA)',
  'AK - 2016',
  'AK - 2019',
  'AK - 2020/Implemented 2020',
  'AL - 2012 (WIDA)',
  'AL - 2013',
  'AL - 2014',
  'AL - 2015',
  'AL - 2016',
  'AL - 2017',
  'AL - 2018',
  'AL - 2019',
  'AL - 2019/Impl. 2020',
  'AL - 2020',
  'AL - 2021/Effective 2022',
  'AR - 2010',
  'AR - 2013',
  'AR - 2014',
  'AR - 2016',
  'AR - 2017',
  'AR - 2018',
  'AR - 2019',
  'AR - 2019/IMPL 2020',
  'AR - 2020/Beginning 2021',
  'AR - 2020/Implements 2020',
  'AR - 2023',
  'AR - Revised 2018',
  'AZ - 2009',
  'AZ - 2015',
  'AZ - 2016',
  'AZ - 2018',
  'AZ - 2019/Implement 2020',
  'AZ - 2022',
  'AZ - 2023',
  'AZAEMA18',
  'AZADULTLA',
  'AZAS',
  'BC',
  'CA - 1998',
  'CA - 2005',
  'CA - 2008',
  'CA - 2010',
  'CA - 2012',
  'CA - 2013',
  'CA - 2018',
  'CA - 2019',
  'CAS',
  'CCMCPMA',
  'CCK12',
  'CCRSA',
  'CCSI',
  'CCSS.MA.2010+',
  'CO',
  'CO - 2012 (WIDA)',
  'CO - 2018',
  'CO - 2020',
  'CO - 2022',
  'CT - 2005',
  'CT - 2006',
  'CT - 2010',
  'CT - 2014',
  'CT - 2015',
  'CT - 2016',
  'CT - 2017',
  'DC - 2007',
  'DC - 2008',
  'DC - 2010',
  'DC - 2011',
  'DC - 2012 (WIDA)',
  'DC - 2013',
  'DC - 2016',
  'DC - 2017',
  'DC - 2019',
  'DE - 1995/Refreshed 2017',
  'DE - 2010',
  'DE - 2010 (CCSS)',
  'DE - 2012 (WIDA)',
  'DE - 2013',
  'DE - 2016',
  'DE - 2018',
  'DE - Health 2007 / PE 2008',
  'DE - Refreshed 2016',
  'FL - 2008',
  'FL - 2010',
  'FL - 2011',
  'FL - 2013',
  'FL - 2014',
  'FL - 2015 (WIDA)',
  'FL - 2016',
  'FL - 2017',
  'FL - 2020',
  'FL - 2021',
  'FL - 2023',
  'FL B.E.S.T. 2020+',
  'FLAS',
  'Functions',
  'GA - 2007',
  'GA - 2008',
  'GA - 2009',
  'GA - 2010',
  'GA - 2011',
  'GA - 2012 (WIDA)',
  'GA - 2013',
  'GA - 2015',
  'GA - 2016',
  'GA - 2017',
  'GA - 2018',
  'GA - 2018/Implement 2019',
  'GA - 2019',
  'GA - 2021',
  'GA - 2023',
  'GAELA2023',
  'HI - 2005',
  'HI - 2007',
  'HI - 2010',
  'HI - 2012 (WIDA)',
  'HI - 2014',
  'HI - 2016',
  'HI - 2018',
  'HI - 2019 (NHES 2007)',
  'HI - 2022',
  'IA - 2012',
  'IA - 2014',
  'IA - 2015',
  'IA - 2016',
  'IA - 2017',
  'IA - 2018',
  'IA - 2019',
  'ID - 2012 (WIDA)',
  'ID - 2015',
  'ID - 2016',
  'ID - 2017',
  'ID - 2018',
  'ID - 2022',
  'IL',
  'IL - 2004',
  'IL - 2009',
  'IL - 2010',
  'IL - 2010 (CCSS) / Implemented 2013',
  'IL - 2012 (WIDA)',
  'IL - 2013',
  'IL - 2014',
  'IL - 2016',
  'IL - 2017',
  'IL - 2019',
  'IL - 2022',
  'IN - 2003',
  'IN - 2008',
  'IN - 2014',
  'IN - 2016',
  'IN - 2017',
  'IN - 2018',
  'IN - 2019',
  'IN - 2020',
  'IN - 2020/Impl 2020',
  'IN - 2020/Implemented 2020',
  'IN - 2022',
  'IN - 2023',
  'KS',
  'KS - 2005',
  'KS - 2010',
  'KS - 2013',
  'KS - 2015',
  'KS - 2016',
  'KS - 2017',
  'KS - 2018',
  'KS - 2019',
  'KS - 2020',
  'KY',
  'KY - 2012 (WIDA)',
  'KY - 2013',
  'KY - 2015',
  'KY - 2018',
  'KY - 2019',
  'KY - 2021',
  'KY - 2022',
  'LA - 2003',
  'LA - 2008',
  'LA - 2010',
  'LA - 2011',
  'LA - 2013',
  'LA - 2016',
  'LA - 2016/Updated 2017',
  'LA - 2017',
  'LA - 2019',
  'LA - 2021',
  'LA - Health 2011 / PE 2009',
  'MA',
  'MA - 1999',
  'MA - 2003',
  'MA - 2010',
  'MA - 2012 (WIDA)',
  'MA - 2015',
  'MA - 2016',
  'MA - 2017',
  'MA - 2018',
  'MA - 2019',
  'MA - 2021',
  'MD',
  'MD - 2008',
  'MD - 2010',
  'MD - 2010/Implemented 2013 (CCSS)',
  'MD - 2012 (WIDA)',
  'MD - 2013',
  'MD - 2015',
  'MD - 2015/Updated 2019',
  'MD - 2016',
  'MD - 2017/Updated 2019',
  'MD - 2018',
  'MD - 2019',
  'MD - 2020',
  'MD - 2020/IMPL 2020',
  'MD - 2022',
  'ME - 2007',
  'ME - 2012 (WIDA)',
  'ME - 2015',
  'ME - 2018',
  'ME - 2019',
  'ME - 2020',
  'ME - 2020/Implemented 2020',
  'ME - 2021',
  'ME - 2022',
  'MI',
  'MI - 2007',
  'MI - 2008',
  'MI - 2010',
  'MI - 2011',
  'MI - 2012 (WIDA)',
  'MI - 2015',
  'MI - 2017',
  'MI - 2019',
  'MI - 2022',
  'MN - 2008',
  'MN - 2009',
  'MN - 2010',
  'MN - 2012',
  'MN - 2012 (WIDA)',
  'MN - 2015',
  'MN - 2016',
  'MN - 2018',
  'MN - 2018/Effective 2021',
  'MN - 2019',
  'MNELA2020',
  'MO - 2007',
  'MO - 2010',
  'MO - 2011',
  'MO - 2012 (WIDA)',
  'MO - 2013',
  'MO - 2016',
  'MO - 2017',
  'MO - 2019',
  'MO - 2021',
  'MS - 2008',
  'MS - 2012 (WIDA)',
  'MS - 2013',
  'MS - 2016',
  'MS - 2017',
  'MS - 2018',
  'MS - 2022',
  'MS Elective Language Arts Courses',
  'MT - 1999',
  'MT - 2009',
  'MT - 2011',
  'MT - 2012 (WIDA)',
  'MT - 2014',
  'MT - 2016',
  'MT - 2020/Effective 2021',
  'NC - 2010',
  'NC - 2012',
  'NC - 2012 (WIDA)',
  'NC - 2013',
  'NC - 2016/IMPL 2016',
  'NC - 2017/Implement 2018',
  'NC - 2017/IMPL 2018',
  'NC - 2019/IMPL 2020',
  'NC - 2020',
  'NC - 2020 (ISTE-S)',
  'NC - 2021/Effective 2021',
  'NCCAS',
  'NCHS',
  'NCSE',
  'NCSS',
  'NCSSC3',
  'NCTMS',
  'NCTE',
  'ND - 2001',
  'ND - 2012',
  'ND - 2012 (WIDA)',
  'ND - 2015',
  'ND - 2017',
  'ND - 2018',
  'ND - 2019',
  'ND - 2021',
  'ND - 2023',
  'ND - 2024',
  'NE - 2010',
  'NE - 2014',
  'NE - 2015',
  'NE - 2016',
  'NE - 2017',
  'NE - 2018',
  'NE - 2019',
  'NE - 2021',
  'NE - 2022',
  'NETSS',
  'NH - 2003',
  'NH - 2005',
  'NH - 2006',
  'NH - 2010',
  'NH - 2012 (WIDA)',
  'NH - 2015',
  'NH - 2016',
  'NH - 2018',
  'NHES',
  'NJ - 2009',
  'NJ - 2012 (WIDA)',
  'NJ - 2013',
  'NJ - 2014',
  'NJ - 2016',
  'NJ - 2020',
  'NJ - 2020/Effective 2021',
  'NJ - 2020/Impl 2021',
  'NJ - 2020/Implement 2021',
  'NJ - 2023',
  'NJ - 2024',
  'NM - 2009',
  'NM - 2012',
  'NM - 2012 (WIDA)',
  'NM - 2013',
  'NM - 2014',
  'NM - 2018',
  'NM - 2019',
  'NM - 2022',
  'NSCG',
  'NV - 2007/Revised 2018',
  'NV - 2010',
  'NV - 2012 (WIDA)',
  'NV - 2014',
  'NV - 2017',
  'NV - 2017/Revised 2018',
  'NV - 2019',
  'NV - 2020/Effective 2021',
  'NV - Information Literacy (2002)',
  'NY - 1996',
  'NY - 2011',
  'NY - 2014/Revised 2017',
  'NY - 2016',
  'NY - 2016/Revised 2017',
  'NY - 2017',
  'NY - 2017/Effective 2020',
  'NY - 2017/Updated 2019',
  'NY - 2018',
  'NY - 2019',
  'NY - 2021',
  'OH - 2009',
  'OH - 2010',
  'OH - 2011',
  'OH - 2012',
  'OH - 2015',
  'OH - 2017',
  'OH - 2018',
  'OH - 2020/Effective 2021',
  'OH - 2022',
  'OK - 2012 (WIDA)',
  'OK - 2015',
  'OK - 2016',
  'OK - 2017',
  'OK - 2018',
  'OK - 2018 (AASL Framework for Learners)',
  'OK - 2019',
  'OK - 2020',
  'OK - 2020/Implemented 2020',
  'OK - 2021',
  'OK - 2022',
  'OK - 2023',
  'OR - 2010',
  'OR - 2014',
  'OR - 2015',
  'OR - 2016',
  'OR - 2017',
  'OR - 2018',
  'OR - 2019',
  'OR - 2019/Effective 2021',
  'OR - 2021',
  'PA - 2002',
  'PA - 2003',
  'PA - 2006',
  'PA - 2009',
  'PA - 2010',
  'PA - 2012',
  'PA - 2014',
  'PA - 2017',
  'PAAS',
  'PRDE',
  'PRDESP',
  'RI - 2003',
  'RI - 2004',
  'RI - 2007',
  'RI - 2010',
  'RI - 2012',
  'RI - 2012 (WIDA)',
  'RI - 2013',
  'RI - 2014',
  'RI - 2016',
  'RI - 2017',
  'RI - 2018',
  'RI - 2019',
  'RI - 2021',
  'RI - 2023',
  'RI - Unknown',
  'SC - 2006',
  'SC - 2015',
  'SC - 2017',
  'SC - 2018',
  'SC - 2019',
  'SC - 2021',
  'SC - 2023',
  'SD',
  'SD - 2010',
  'SD - 2012 (WIDA)',
  'SD - 2014',
  'SD - 2015',
  'SD - 2017',
  'SD - 2018',
  'SD - 2018/Effective 2019',
  'SD - 2019',
  'SD - 2021',
  'SD - 2022',
  'SHAPE.2013+',
  'TAK',
  'TN - 2008',
  'TN - 2013',
  'TN - 2014',
  'TN - 2016',
  'TN - 2017',
  'TN - 2018',
  'TN - 2021',
  'TN - 2022',
  'TX - 1998',
  'TX - 1998/Amended 2008',
  'TX - 2007',
  'TX - 2007/Effective 2007',
  'TX - 2010',
  'TX - 2010 (Amended 2011)',
  'TX - 2011',
  'TX - 2012',
  'TX - 2013',
  'TX - 2014',
  'TX - 2015',
  'TX - 2016',
  'TX - 2017',
  'TX - 2017/Effective 2017',
  'TX - 2017/Effective 2019',
  'TX - 2018',
  'TX - 2018/Effective 2020',
  'TX - 2019',
  'TX - 2020',
  'TX - 2020/Effective 2020',
  'TX - 2022',
  'UT - 2010',
  'UT - 2012 (WIDA)',
  'UT - 2013',
  'UT - 2014',
  'UT - 2015',
  'UT - 2016',
  'UT - 2016/Implement 2016',
  'UT - 2018',
  'UT - 2019',
  'UT - 2019/Implement 2019',
  'UT - 2020',
  'UT - 2022',
  'UT - 2023',
  'VA - 1998',
  'VA - 2009',
  'VA - 2012 (WIDA)',
  'VA - 2014',
  'VA - 2015',
  'VA - 2016',
  'VA - 2017',
  'VA - 2018',
  'VA - 2019',
  'VA - 2020',
  'VA - 2020/Impl. 2020',
  'VA - 2020/Implemented 2020',
  'VA - 2021',
  'VA - 2022',
  'VA - 2023',
  'VA - 2024',
  'VT - 2010',
  'VT - 2010 (CCSS)',
  'VT - 2012 (WIDA)',
  'VT - 2014',
  'VT - 2015',
  'VT - 2016',
  'VT - 2017',
  'VT - 2018/IMPL 2018 (Jump$tart)',
  'VT - 2019',
  'WA - 2010',
  'WA - 2011',
  'WA - 2012',
  'WA - 2014',
  'WA - 2015',
  'WA - 2016',
  'WA - 2017',
  'WA - 2018',
  'WA - 2019',
  'WASS2008',
  'WI - 2010',
  'WI - 2012',
  'WI - 2013/updated 2017',
  'WI - 2016',
  'WI - 2017',
  'WI - 2018',
  'WI - 2019',
  'WI - 2020',
  'WI - 2020/Implement 2021',
  'WI - 2021',
  'WV - 2016',
  'WV - 2017',
  'WV - 2018',
  'WV - 2019',
  'WV - 2021',
  'WV - 2023',
  'WY - 2012',
  'WY - 2012 (WIDA)',
  'WY - 2013',
  'WY - 2014',
  'WY - 2014/Updated 2018',
  'WY - 2016',
  'WY - 2018',
  'WY - 2020',
  'WY - 2022',
  'fl2020ma',
];

// ============================================================================
// SPINE Node Navigation Types
// ============================================================================

export interface SpineNodeSubject {
  nodeId: string;
  name: string;
  description?: string;
}

export interface SpineNodeTree {
  nodeId: string;
  name: string;
  description?: string;
  nodeType: string;
  hasChildren?: boolean;
  competency?: boolean;
  competencyParent?: boolean;
  children?: SpineNodeTree[];
}

export interface CompetencyResult {
  identifier: string;           // UUID of the competency
  parentId: string;            // UUID of parent node
  title: string;               // Competency title (contains all searched keywords)
  subtitle?: string;           // Optional subtitle
  uri: string;                 // Hierarchical URI (contains the subject filter)
  subject: string;             // Subject name (e.g., "MATHEMATICS", "SCIENCE")
  domain: string;              // Domain within subject (e.g., "Algebra", "Biology")
  cluster: string;             // Cluster within domain
  hasChildren: boolean;        // Always false for competencies
  competency: boolean;         // Always true for competencies
  competencyParent: boolean;   // Always false for competencies
  created: string;             // ISO date string
  lastModified: string;        // ISO date string
  version: string;             // Version number
}

export interface CompetencySearchResponse {
  data: CompetencyResult[];
  success: boolean;
  message?: string;
}

// ============================================================================
// Semantic Standards Search
// ============================================================================

// Dynamic filter metadata from /standards/filters endpoint
export interface FilterMetadata {
  name: string;
  type: 'string[]' | 'int[]';
  values: (string | number)[];
  description: string;
}

export interface FiltersResponse {
  filters: FilterMetadata[];
}

// Semantic search filters - dynamic, keys come from FilterMetadata.name
export interface SemanticSearchFilters {
  [key: string]: (string | number)[];
}

export interface SemanticSearchRequest {
  query: string;
  filters?: SemanticSearchFilters;
  threshold?: number;
  limit?: number;
}

export interface SemanticSearchResult {
  standardId: string;
  score: number;
  status: string;
  grades: string[];
  documentCode: string;
  standardJurisdiction: string;
  usStatePostalCode: string | null;
  subjectCode: string;
  domainDescription: string;
  clusterDescription: string;
  standardItemType: string;
  itemCode: string;
  itemDescription: string;
  isDefault: boolean;
  standardParentId: string;
  ancestors: string[];
}

export interface SemanticSearchResponse {
  results: SemanticSearchResult[];
  query: string;
  total: number;
}
